import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import Connect4Board from './Connect4Board';
import { safeString } from '../../utils/safeString';
import {
  createInitialGameState,
  isValidMove,
  makeMove,
  getGameStatus,
  getNextTurn,
} from './logic';
import {
  subscribeToGame,
  updateGameState,
  updateGameStatus,
  requestRematch,
  resetGameForRematch,
  saveGameToHistory,
  updateMatchStats,
  deleteGame,
  checkBothRematchRequested,
} from '../../services/gameService';
import { addRecentOpponent } from '../../services/friendService';
import { leaveRoom } from '../../services/roomService';
import { database } from '../../services/firebase';
import { ref, update, serverTimestamp } from 'firebase/database';
import './Connect4.css';

const Connect4 = ({ roomCode, roomData, user }) => {
  console.log('Connect4 rendering:', { roomCode, roomData, user });

  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [opponentRematchRequested, setOpponentRematchRequested] = useState(false);
  const [error, setError] = useState(null);
  const confettiShownRef = useRef(false);
  const gameRecordedRef = useRef(false);

  // Validate props
  if (!roomCode || !roomData || !user) {
    console.error('Missing required props:', { roomCode, roomData, user });
    return (
      <div className="connect4-container">
        <div className="error-message">Missing required data. Please try again.</div>
      </div>
    );
  }

  const isHost = roomData.host && roomData.host.userId === user.uid;
  const opponent = isHost ? roomData.guest : roomData.host;

  // If opponent left, show waiting state
  if (!opponent) {
    const handleReturnToLobby = async () => {
      try {
        const roomRef = ref(database, `rooms/${roomCode}`);
        await update(roomRef, {
          status: 'waiting',
          gameSelected: null,
          lastActivity: serverTimestamp()
        });
        navigate(`/room/${roomCode}`);
      } catch (error) {
        console.error('Error returning to lobby:', error);
        navigate(`/room/${roomCode}`);
      }
    };

    return (
      <div className="connect4-container">
        <div className="game-header">
          <h2>Connect 4</h2>
        </div>
        <div className="player-waiting-state">
          <div className="waiting-spinner"></div>
          <h3>Player left the room</h3>
          <p>Waiting for player to return...</p>
          <p className="waiting-hint">The game will resume when they come back</p>
          <button className="leave-button" onClick={handleReturnToLobby}>
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  // Get player colors from game state
  const myColor = gameState ? (isHost ? gameState.hostColor : gameState.guestColor) : null;
  const opponentColor = myColor === 'red' ? 'yellow' : 'red';

  console.log('Player info:', { isHost, opponent, myColor, opponentColor });

  // Subscribe to game updates
  useEffect(() => {
    console.log('Setting up game subscription for room:', roomCode);

    try {
      const unsubscribe = subscribeToGame(roomCode, async (game) => {
        console.log('Game update received:', game);

        if (game && game.gameState) {
          console.log('Setting gameState to:', game.gameState);
          setGameState(game.gameState);

          // Check rematch requests
          if (game.rematchRequests) {
            const myRequest = !!game.rematchRequests[user.uid];
            const opponentRequest = !!game.rematchRequests[opponent.userId];

            setRematchRequested(myRequest);
            setOpponentRematchRequested(opponentRequest);

            // If both players requested rematch, only the host resets the game
            if (myRequest && opponentRequest && isHost && game.gameState.isGameOver) {
              console.log('Both players requested rematch, host is resetting game...');

              const newGameState = createInitialGameState(
                roomData.host.userId,
                roomData.guest.userId
              );

              await resetGameForRematch(
                roomCode,
                'connect4',
                newGameState,
                {
                  host: roomData.host,
                  guest: roomData.guest,
                }
              );
            }
          } else {
            setRematchRequested(false);
            setOpponentRematchRequested(false);
          }
        } else if (game === null) {
          console.warn('Game not found in database');
          setError('Game not found. It may not have been created yet.');
        } else if (game && !game.gameState) {
          console.error('Game exists but has no gameState property');
          setError('Game data is missing gameState. Please try restarting the game.');
        }

        setLoading(false);
      });

      return () => {
        console.log('Cleaning up game subscription');
        unsubscribe();
      };
    } catch (err) {
      console.error('Error setting up game subscription:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [roomCode, user.uid, opponent.userId, isHost, roomData]);

  // Trigger confetti on win (only once per game)
  useEffect(() => {
    if (gameState && gameState.isGameOver && !gameState.isDraw && !confettiShownRef.current) {
      const iWon = gameState.winner === myColor;

      if (iWon) {
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
          colors: ['#FF6B6B', '#FFD93D', '#6BCF7F'],
        });
        confettiShownRef.current = true;
      }
    }

    // Reset the flags when game restarts (not game over anymore)
    if (gameState && !gameState.isGameOver) {
      confettiShownRef.current = false;
      gameRecordedRef.current = false;
    }
  }, [gameState, myColor]);

  // Save game to history when it ends (only once)
  useEffect(() => {
    if (gameState && gameState.isGameOver && !gameRecordedRef.current) {
      gameRecordedRef.current = true;

      const iWon = gameState.winner === myColor;
      const result = gameState.isDraw ? 'draw' : iWon ? 'won' : 'lost';

      // Convert winner color to user ID (if not a draw)
      const winnerUserId = gameState.isDraw
        ? 'draw'
        : (gameState.winner === myColor ? user.uid : opponent.userId);

      // Save to game history
      saveGameToHistory(user.uid, {
        gameType: 'connect4',
        opponent: opponent.displayName,
        opponentId: opponent.userId,
        result,
        winner: winnerUserId,
        isDraw: gameState.isDraw,
        roomCode,
      });

      // Update match stats
      updateMatchStats(user.uid, 'connect4', result);

      // Add to recent opponents
      addRecentOpponent(
        user.uid,
        opponent.userId,
        opponent.displayName,
        opponent.photoURL,
        'connect4',
        result
      );

      // Update game status
      updateGameStatus(roomCode, 'completed');
    }
  }, [gameState, myColor, user.uid, opponent, roomCode]);

  const handleColumnClick = async (col) => {
    if (!gameState || gameState.isGameOver) return;

    // Check if it's my turn
    if (gameState.currentTurn !== myColor) {
      return;
    }

    // Validate move
    if (!isValidMove(gameState.board, col)) {
      return;
    }

    // Make move
    const { board: newBoard, row } = makeMove(gameState.board, col, myColor);

    if (row === null) {
      return; // Move failed
    }

    // Check game status
    const status = getGameStatus(newBoard);

    // Update game state
    const newGameState = {
      ...gameState,
      board: newBoard,
      currentTurn: getNextTurn(gameState.currentTurn),
      moveCount: gameState.moveCount + 1,
      lastMove: { row, col },
      isGameOver: status.isOver,
      winner: status.winner,
      isDraw: status.isDraw,
      winningCombination: status.winningCombination,
      direction: status.direction,
    };

    // Update Firebase
    await updateGameState(roomCode, newGameState);
  };

  const handleRematch = async () => {
    // Just request the rematch - the subscription will handle the reset
    await requestRematch(roomCode, user.uid);
    // Local state will be updated by the subscription
  };

  const handleLeaveGame = async () => {
    try {
      // Update room status back to waiting and clear game selections
      const roomRef = ref(database, `rooms/${roomCode}`);
      await update(roomRef, {
        status: 'waiting',
        gameSelected: null,
        'host/gameSelection': null,
        'guest/gameSelection': null,
        lastActivity: serverTimestamp()
      });
      // Navigate back to room lobby
      navigate(`/room/${roomCode}`);
    } catch (error) {
      console.error('Error leaving game:', error);
      // Navigate to room lobby anyway
      navigate(`/room/${roomCode}`);
    }
  };

  if (loading) {
    return (
      <div className="connect4-container">
        <div className="loading-message">
          <div className="spinner-large"></div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="connect4-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={handleLeaveGame}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="connect4-container">
        <div className="error-message">
          <p>Game not found or not yet created</p>
          <p>Please wait a moment or return to lobby</p>
          <button onClick={handleLeaveGame}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  // Additional validation for gameState
  if (!gameState.board || !Array.isArray(gameState.board)) {
    console.error('Invalid gameState - missing or invalid board:', gameState);
    return (
      <div className="connect4-container">
        <div className="error-message">
          <p>Invalid game state</p>
          <p>The game data is corrupted or incomplete</p>
          <button onClick={handleLeaveGame}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  const isMyTurn = gameState.currentTurn === myColor;
  const iWon = gameState.isGameOver && gameState.winner === myColor;
  const iLost = gameState.isGameOver && gameState.winner === opponentColor;

  return (
    <div className="connect4-container">
      {/* Header */}
      <div className="game-header">
        <h1 className="game-title">🔴 Connect 4 🟡</h1>
      </div>

      {/* Players Info */}
      <div className="players-info">
        <div className={`player-card ${isMyTurn && !gameState.isGameOver ? 'active' : ''}`} data-color={myColor}>
          <img src={user.photoURL} alt={user.displayName} className="player-avatar" />
          <div className="player-details">
            <p className="player-name">
              You <span className={`color-badge color-${myColor}`}>{myColor === 'red' ? '🔴' : '🟡'}</span>
            </p>
          </div>
        </div>

        <div className="vs-divider">VS</div>

        <div className={`player-card ${!isMyTurn && !gameState.isGameOver ? 'active' : ''}`} data-color={opponentColor}>
          <img src={safeString(opponent?.photoURL, '')} alt={safeString(opponent?.displayName, 'Opponent')} className="player-avatar" />
          <div className="player-details">
            <p className="player-name">
              {safeString(opponent?.displayName, 'Opponent')} <span className={`color-badge color-${opponentColor}`}>{opponentColor === 'red' ? '🔴' : '🟡'}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Turn Indicator */}
      {!gameState.isGameOver && (
        <div className="turn-indicator-section">
          <p className="turn-indicator">
            {isMyTurn ? "Your Turn!" : `${safeString(opponent?.displayName, 'Opponent')}'s Turn`}
          </p>
        </div>
      )}

      {/* Game Status */}
      {!gameState.isGameOver && (
        <div className="game-status">
          <p className="status-text">
            {isMyTurn ? "It's your turn! Drop a disc!" : `Waiting for ${safeString(opponent?.displayName, 'opponent')}...`}
          </p>
        </div>
      )}

      {/* Game Board */}
      <Connect4Board
        board={gameState.board}
        onColumnClick={handleColumnClick}
        winningCombination={gameState.winningCombination}
        disabled={!isMyTurn || gameState.isGameOver}
      />

      {/* Game Over */}
      {gameState.isGameOver && (
        <div className="game-over">
          <div className={`result-message ${iWon ? 'won' : iLost ? 'lost' : 'draw'}`}>
            {gameState.isDraw ? (
              <>
                <span className="result-icon">🤝</span>
                <h2>It's a Draw!</h2>
                <p>Great game! Want to play again?</p>
              </>
            ) : iWon ? (
              <>
                <span className="result-icon">🎉</span>
                <h2>You Won!</h2>
                <p>Congratulations on your victory!</p>
              </>
            ) : (
              <>
                <span className="result-icon">😞</span>
                <h2>You Lost</h2>
                <p>Better luck next time!</p>
              </>
            )}
          </div>

          {/* Rematch Section */}
          <div className="rematch-section">
            {!rematchRequested && !opponentRematchRequested && (
              <button className="rematch-button" onClick={handleRematch}>
                🔄 Request Rematch
              </button>
            )}

            {rematchRequested && !opponentRematchRequested && (
              <div className="rematch-status">
                <p>⏳ Waiting for {safeString(opponent?.displayName, 'opponent')} to accept rematch...</p>
              </div>
            )}

            {!rematchRequested && opponentRematchRequested && (
              <div className="rematch-status">
                <p>🎮 {safeString(opponent?.displayName, 'Opponent')} wants a rematch!</p>
                <button className="rematch-button accept" onClick={handleRematch}>
                  ✓ Accept Rematch
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Move Counter */}
      <div className="move-counter">
        <p>Moves: {gameState.moveCount}</p>
      </div>

      {/* Leave Game Button - Always Visible */}
      <div className="leave-game-section">
        <button className="leave-button" onClick={handleLeaveGame}>
          Leave Game
        </button>
      </div>
    </div>
  );
};

export default Connect4;
